import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';

// 语言数据
const translations = {
  zh: {
    nav: {
      home: '首页',
      about: '关于我',
      experience: '经历',
      skills: '技能',
      games: '游戏体验',
      portfolio: '作品集',
      gallery: '图库',
      contact: '联系',
      contactMe: '联系我',
    },
    home: {
      tagline: '专注感知算法 · 三维重建 / 视觉模型 / 数据驱动',
      greeting: '你好，我是',
      intro: 'UNSW 信息技术（人工智能方向）硕士毕业，聚焦计算机视觉、感知算法与三维重建。擅长 AI 驱动开发，深度整合 AI 工作流赋能全链路研发，灵活调用智能工具链快速构建定制化解决方案，将复杂的逻辑验证、算法原型及数据可视化周期压缩至小时级，实现敏捷开发与高效交付。',
      contactBtn: '联系我',
      viewWorkBtn: '查看作品',
      stats: [
        { label: '感知&三维项目', value: '5+', desc: 'SKU 三维重建 / AIGC 游戏 Demo / FPS 技术验证' },
        { label: '工程落地', value: '3+', desc: 'AI 标注平台 / 仿真工具链 / 教学自动化' },
        { label: '技术栈', value: 'Python / C# / C++ / Unity', desc: '多模态感知与 3D 数字孪生' },
      ],
    },
    about: {
      title: '关于我',
      desc1: '我在 UNSW 攻读信息技术硕士（人工智能方向），核心方向为计算机图像、神经网络、计算机视觉与机器学习。当前在大咖机器人实习，参与 SKU 三维重建管线与 AI 标注平台（SAM2）的设计与落地，将鲁棒的感知算法与可用的工程工具链打通。',
      desc2: '除工业级感知项目外，我也独立主导多款技术向 Demo：如 Unity 独立游戏《空天纪元》与 FPS Demo《光蚀》，在 3D 资产生成（AIGC）、仿真环境搭建与数据化调参方面有完整实践。习惯用可视化调试与数据指标驱动迭代，把抽象的算法与体验转化为可以「看得见、调得动」的系统。',
      contactTitle: '联系 / 快速响应',
      availability: '作为应届感知算法工程师候选人，正在积极寻找计算机视觉 / 三维重建 / 感知算法相关实习或全职机会，期待与您就算法实现、工具链搭建与系统落地深入交流。',
      infoCards: [
        { label: '姓名', value: '马君羽' },
        { label: '目标岗位', value: '感知算法工程师 / 计算机视觉工程师' },
        { label: '毕业时间', value: '2026 年 02 月' },
        { label: '技术标签', value: 'Python / PyTorch / 3D 重建 / Unity / C++' },
      ],
    },
    experience: {
      title: '经历',
      badge: '项目 · 教育 · 交付',
    },
    games: {
      title: '游戏体验',
      subtitle: '常玩与深度体验的游戏，支撑我对节奏与手感的理解',
      badge: '玩家视角 · 体验拆解',
      playRecord: '游玩记录',
      prevGame: '上一组游戏',
      nextGame: '下一组游戏',
    },
    skills: {
      title: '技能树',
      otherSkills: '其他技能',
    },
    portfolio: {
      title: '作品集',
      viewDetails: '查看详情',
    },
    gallery: {
      title: '图库与视频',
      video: '视频',
      image: '图片',
      clickToEnlarge: '点击放大',
    },
    contact: {
      title: '联系我',
      desc: '如果您正在寻找具备工程落地能力的感知算法/计算机视觉人才，或在三维重建、数字孪生、仿真平台与 AI 工具链等方向有合作机会，欢迎邮件联系。我正在积极寻找相关实习或全职岗位，承诺 24 小时内回复。',
      leaveMessage: '留言给我',
      yourName: '您的姓名',
      yourEmail: '您的邮箱',
      yourMessage: '您的留言',
      sending: '发送中...',
      sendMessage: '发送消息',
      successMessage: '留言已发送！我会尽快回复您。',
      errorMessage: '发送失败：{error}。请直接发邮件至 junyu.ma.resume@gmail.com',
    },
    footer: {
      copyright: '保留所有权利',
      tags: ['感知算法', '三维重建'],
    },
    lightbox: {
      close: '关闭',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      experience: 'Experience',
      skills: 'Skills',
      games: 'Gaming',
      portfolio: 'Portfolio',
      gallery: 'Gallery',
      contact: 'Contact',
      contactMe: 'Contact Me',
    },
    home: {
      tagline: 'Perception Algorithms · 3D Reconstruction / Vision Models / Data-Driven',
      greeting: 'Hello, I\'m',
      intro: 'Master of IT (AI stream) graduate from UNSW, focusing on computer vision, perception algorithms, and 3D reconstruction. I specialize in AI-driven development, tightly integrating AI workflows into end-to-end pipelines and flexibly leveraging intelligent toolchains to rapidly build customized solutions, compressing logic verification, algorithm prototyping, and data visualization cycles to hours for agile development and efficient delivery.',
      contactBtn: 'Contact Me',
      viewWorkBtn: 'View Work',
      stats: [
        { label: 'Perception & 3D Projects', value: '5+', desc: 'SKU 3D reconstruction / AIGC game demo / FPS technical validations' },
        { label: 'Engineering Deliverables', value: '3+', desc: 'AI annotation platform / simulation toolchain / teaching automation' },
        { label: 'Tech Stack', value: 'Python / C# / C++ / Unity', desc: 'Multimodal perception & 3D digital twins' },
      ],
    },
    about: {
      title: 'About Me',
      desc1: 'I\'m pursuing a Master of Information Technology (AI specialisation) at UNSW, with core studies in computer graphics, neural networks, computer vision, and machine learning. Currently interning at Daka Robotics, working on SKU 3D reconstruction pipelines and a SAM2-based annotation platform, bridging robust perception algorithms with usable engineering toolchains.',
      desc2: 'Beyond industrial perception projects, I independently lead several technical demos such as the Unity game "Sky Era" and the FPS demo "Light Erosion." I have hands-on experience in AIGC asset generation, simulation environment setup, and data-driven parameter tuning, habitually using visual debugging and metrics to turn abstract algorithms and experiences into systems that are tangible, tunable, and traceable.',
      contactTitle: 'Contact / Quick Response',
      availability: 'As an entry-level perception algorithm engineer candidate, I\'m actively seeking internship or full-time roles in computer vision, 3D reconstruction, and perception. I\'d love to discuss algorithm implementation, toolchain design, and system deployment with you and learn from your expertise.',
      infoCards: [
        { label: 'Name', value: 'Junyu Ma' },
        { label: 'Target Role', value: 'Perception Algorithm Engineer / Computer Vision Engineer' },
        { label: 'Graduation', value: 'February, 2026' },
        { label: 'Tech Tags', value: 'Python / PyTorch / 3D Reconstruction / Unity / C++' },
      ],
    },
    experience: {
      title: 'Experience',
      badge: 'Projects · Education · Delivery',
    },
    games: {
      title: 'Gaming Experience',
      subtitle: 'Games I play and deeply understand, supporting my insights into pacing and feel',
      badge: 'Player Perspective · Experience Analysis',
      playRecord: 'Play Record',
      prevGame: 'Previous Game',
      nextGame: 'Next Game',
    },
    skills: {
      title: 'Skills',
      otherSkills: 'Other Skills',
    },
    portfolio: {
      title: 'Portfolio',
      viewDetails: 'View Details',
    },
    gallery: {
      title: 'Gallery & Videos',
      video: 'Video',
      image: 'Image',
      clickToEnlarge: 'Click to Enlarge',
    },
    contact: {
      title: 'Contact Me',
      desc: 'If you\'re looking for perception or computer vision talent with strong engineering skills, or have collaboration opportunities in 3D reconstruction, digital twins, simulation platforms, or AI toolchains, please feel free to reach out. I\'m actively seeking relevant internship or full-time roles and will reply within 24 hours.',
      leaveMessage: 'Leave a Message',
      yourName: 'Your Name',
      yourEmail: 'Your Email',
      yourMessage: 'Your Message',
      sending: 'Sending...',
      sendMessage: 'Send Message',
      successMessage: 'Message sent! I\'ll reply as soon as possible.',
      errorMessage: 'Failed to send: {error}. Please email directly to junyu.ma.resume@gmail.com',
    },
    footer: {
      copyright: 'All Rights Reserved',
      tags: ['Perception Algorithms', '3D Reconstruction'],
    },
    lightbox: {
      close: 'Close',
    },
  },
};

// 获取翻译数据的辅助函数
const getExperiences = (lang) => {
  if (lang === 'en') {
    return [
      {
        date: '2026.02 - Present',
        title: 'Perception Algorithm Engineer Intern',
        subtitle: 'Daka Robotics (Beijing)',
        bullets: [
          'Built a multimodal SKU 3D reconstruction pipeline covering both warehouse/棚-level scenes and individual products, integrating NeRF, Kiri Engine, and Mipmap techniques to produce high-fidelity digital assets.',
          'Developed a Blender-based smart low-poly plugin with automated retopology and decimation, converting high-poly models into simulation-ready low-poly assets while preserving key geometry and ensuring stable frame rates in physics engines.',
          'Designed a SAM2-powered annotation platform (Vibe Coding) that unifies 2D fine segmentation with 9D spatial labeling, significantly improving data quality and efficiency for Isaac Sim-based grasping strategy training and sim-to-real transfer.',
        ],
      },
      {
        date: '2026.02 - 2026.03',
        title: '"Sky Era" – Independent Unity Game Development',
        subtitle: 'Solo Developer · Design / Architecture / AIGC Pipeline',
        bullets: [
          'Redesigned an AIGC content pipeline combining Google Banana style harmonization, Tripo/Rodin rapid modeling, and Hunyuan image-to-video, forming an automated chain from concept to 3D assets to cinematic videos.',
          'Built a decoupled Unity architecture with UnityEvent and Singleton patterns to seamlessly orchestrate NPCs, combat, and audio systems, and used modular state machines plus NavMesh AI for dual-mode gameplay and rapid numerical iteration via ScriptableObject configs.',
          'Implemented custom Editor tools for visual keyframe camera editing and film-like camera motion, delivering a high-completion playable demo validating the core gameplay loop and technical architecture.',
        ],
      },
      {
        date: '2025.09 - 2025.11',
        title: 'Independent Game Development · Light Erosion Demo',
        subtitle: 'UNSW Course Project',
        bullets: [
          'Modular weapon and projectile system with AI behavior trees, enabling minute-level combat pacing adjustments.',
          'Innovative dual-layer collision capsule system that completely solves AI clipping issues, significantly improving level fluidity.',
          'Project achieved HD score of 91.3/100, combining performance, gameplay, and engineering maturity.',
        ],
      },
      {
        date: '2024.02 - 2026.02',
        title: 'Master of Information Technology',
        subtitle: 'University of New South Wales (UNSW)',
        bullets: [
          'Core: Computer Graphics / Neural Networks / Vision / Machine Learning / Databases',
          'Practice: RadianceAscending (3D Roguelike FPS) technical level design; CNS education platform frontend interaction and system upgrade',
        ],
      },
      {
        date: '2024.09 - 2024.10',
        title: 'Team Game Development | 3D Card-Based Combat Demo',
        subtitle: '2024 Spotlight Game Jam Challenge',
        bullets: [
          'Designed a core "Light Activation" mechanic where players manipulate light sources to awaken cards, reveal enemy intent, and trigger synergy bonuses.',
          'Integrated spiral hand interaction with volumetric lighting to transform "light" from visual flair into strategic gameplay.',
          'Implemented physically-aware light interaction via collision detection, making the abstract theme tangible and player-controllable.',
        ],
      },
      {
        date: '2023.10 - 2024.01',
        title: 'English Teaching Assistant',
        subtitle: 'Shangxin International Education',
        bullets: [
          '60+ hours of specialized oral coaching, optimizing pronunciation, grammar, and test strategies',
          'Python + Excel automation for 200+ weekly reports, quantitatively tracking learning progress',
        ],
      },
      {
        date: '2021.07 - Present',
        title: 'Network Equipment Assembly Technician',
        subtitle: 'Xuefu Graduate School',
        bullets: [
          'Diagnosed legacy network (1 switch + 4 routers) for 80 students/≈160 terminals, found single router stable limit ≈10 devices, high-concurrency video stutter rate >40%, Apple devices frequently disconnected due to channel conflicts',
          'Designed and implemented solution: 2 AX6000 + Gigabit switch mesh network, seamless roaming + QoS rate limiting; used NetSpot to scan blind spots/interference, adjusted channels to low-conflict bands',
          'Results: 100% coverage (edge signal improved from <-80dBm to ≥-65dBm), 20-device simultaneous HD video stutter rate 40%→0%, Apple disconnection rate 50%→5%',
        ],
      },
      {
        date: '2018.09 - 2022.06',
        title: 'Computer Science and Technology',
        subtitle: 'Guangdong Polytechnic Normal University',
        bullets: [
          'Thesis: WeChat Mini Program canteen ordering platform (frontend + backend + database design)',
          'Related courses: Data Structures, JAVA Programming, Software Engineering, Computer Networks, Computer Organization',
        ],
      },
    ];
  }
  return [
    {
      date: '2026.02 - 至今',
      title: '感知算法工程师实习生',
      subtitle: '大咖机器人（北京）',
      bullets: [
        '搭建覆盖仓库/货架宏观场景与单 SKU 微观细节的多模态三维重建管线，整合 NeRF、Kiri Engine 与 Mipmap 技术，实现高保真数字化资产。',
        '自研 Blender 智能低模插件，集成自动拓扑与减面算法，将高模一键转为适配物理仿真的智能 Low-Poly，解决大规模场景导入仿真引擎的性能瓶颈，在保持几何特征的前提下稳定仿真帧率。',
        '基于 SAM2 搭建一体化标注平台（Vibe Coding），打通 2D 精细分割与 9D 空间标注：通过 Bounding Box + 自动 Mask 的两阶段标注流程，将单图标注效率提升一个量级，并为 Isaac Sim 抓取策略训练提供高质量数据，显著提升 Sim-to-Real 迁移成功率。',
      ],
    },
    {
      date: '2026.02 - 2026.03',
      title: '《空天纪元》独立游戏开发',
      subtitle: '唯一主创 · 玩法设计 / 系统架构 / AIGC 管线',
      bullets: [
        '重构 AIGC 资产生产管线，整合 Google Banana 风格统一、Tripo/Rodin 快速建模与混元图生视频，实现从概念到 3D 资产再到动态视频的自动化生产链路，大幅提升美术资源迭代效率。',
        '基于 UnityEvent 与单例模式构建高解耦架构，联动 NPC、战斗与音频系统；设计模块化状态机管理双模式切换，结合 NavMesh 智能 AI 与 ScriptableObject 配置驱动支持数值快速迭代。',
        '自研 Editor 工具实现关键帧相机可视化编辑与电影级运镜，最终交付包含深度叙事和高自由度交互的高完成度 Demo，验证核心玩法循环与技术架构可行性。',
      ],
    },
    {
      date: '2025.09 - 2025.11',
      title: '独立游戏开发 · 光蚀 Demo',
      subtitle: 'UNSW 课程项目',
      bullets: [
        '模块化武器与抛射物系统，配合AI行为树，实现战斗节奏分钟级调优。',
        '创新采用双层碰撞胶囊体，彻底解决AI穿模问题，大幅提升关卡流畅度。',
        '项目实测获HD综合评分91.3/100，兼具性能、玩法与工程成熟度。',
      ],
    },
    {
      date: '2024.02 - 2026.03',
      title: 'Master of Information Technology',
      subtitle: '新南威尔士大学（UNSW）',
      bullets: [
        '核心：计算机图像 / 神经网络 / 视觉 / 机器学习 / 数据库',
        '实践：RadianceAscending(3D 肉鸽 FPS) 技术型关卡设计；CNS 教育平台前端交互与系统升级',
      ],
    },
    {
      date: '2024.09 - 2024.10',
      title: '团队游戏开发 | 3D卡牌战斗游戏 Demo',
      subtitle: '2024聚光灯游戏创作挑战',
      bullets: [
        '设计“光照激活”核心机制，玩家通过操控光源唤醒卡牌、揭示敌意并触发协同加成。',
        '融合螺旋手牌交互与体积光渲染，将“光”从视觉元素转化为策略玩法载体。',
        '基于物理碰撞实现可感光照交互，使抽象主题成为可操作的游戏实体。',
      ],
    },
    {
      date: '2023.10 - 2024.01',
      title: '英语助教',
      subtitle: '上新国际教育',
      bullets: [
        '60+ 小时口语专项辅导，优化发音、语法与应试策略',
        'Python + Excel 自动化 200+ 份周报，量化跟踪学习进度',
      ],
    },
    {
      date: '2021.07 - 至今',
      title: '网络设备装配技术员',
      subtitle: '学府考研',
      bullets: [
        '面向 80 名学生/≈160 台终端诊断旧网（1 交换机+4 路由），发现单路由稳定上限≈10 台，高并发视频卡顿率 >40%，苹果设备因信道冲突频繁掉线',
        '设计并落地方案：2 台 AX6000 + 千兆交换机组网，Mesh 无缝漫游 + QoS 限速均衡；用 NetSpot 全域扫盲区/干扰，调信道到低冲突频段',
        '结果：覆盖率 100%（边缘信号由 <-80dBm 提升至 ≥-65dBm），20 台终端同时高清视频卡顿率 40%→0%，苹果断连率 50%→5%',
      ],
    },
    {
      date: '2018.09 - 2022.06',
      title: '计算机科学与技术',
      subtitle: '广东技术师范大学',
      bullets: ['毕业论文：基于微信小程序的食堂订餐平台（前后端 + 数据库设计）','相关课程：数据结构、JAVA编程、软件工程、计算机网络、计算机组成原理'],
    },
  ];
};

const getSkills = (lang) => {
  if (lang === 'en') {
    return [
      { 
        name: 'Python / PyTorch', 
        value: 90,
        description: 'Experienced in building computer vision and perception pipelines, from data preprocessing and training to evaluation and deployment, using Python and PyTorch in real-world projects such as SKU 3D reconstruction and SAM2-based annotation platforms.'
      },
      { 
        name: '3D Reconstruction & Digital Twins', 
        value: 88,
        description: 'Designed multimodal 3D reconstruction workflows combining NeRF, Kiri Engine, and Mipmap for SKU and warehouse-level assets, with attention to mesh quality, texture fidelity, and performance constraints in simulation engines.'
      },
      { 
        name: 'Unity / C# / Tool Development', 
        value: 86,
        description: 'Built highly decoupled Unity architectures with UnityEvent, ScriptableObject, and custom Editor tooling, enabling rapid iteration of gameplay, AI behavior, and cinematic camera systems in projects like "Sky Era".'
      },
      { 
        name: 'Computer Vision & Perception', 
        value: 84,
        description: 'Solid foundation in image processing, segmentation, and multi-view geometry, with coursework in computer graphics, neural networks, and vision, and practical experience building SAM2-assisted annotation tools and visual debugging pipelines.'
      },
      { 
        name: 'English & Engineering Literacy', 
        value: 80,
        description: 'PTE 69, comfortable reading research papers and official documentation (e.g., Unreal Engine, vision frameworks), and collaborating in English-speaking teams. Familiar with Docker basics and proficient in Git for version control and team workflows.'
      },
      { 
        name: 'Data-Driven Development & Automation', 
        value: 78,
        description: 'Used Python + Excel and other automation tools to generate 200+ weekly learning reports, and habitually design monitoring/visualization components to make algorithm performance and system health observable and tunable.'
      },
    ];
  }
  return [
    { 
      name: 'Python / PyTorch', 
      value: 90,
      description: '在感知与视觉项目中使用 Python 与 PyTorch 完成从数据处理、模型训练到评估与部署的完整流程，参与 SKU 三维重建与基于 SAM2 的标注平台等实际工程项目。'
    },
    { 
      name: '三维重建与数字孪生', 
      value: 88,
      description: '设计并落地结合 NeRF、Kiri Engine 与 Mipmap 的三维重建流程，服务于仓库/货架与单体 SKU 资产数字化，兼顾网格质量、纹理细节与仿真引擎性能。'
    },
    { 
      name: 'Unity / C# / 工具开发', 
      value: 86,
      description: '在《空天纪元》等项目中基于 UnityEvent、ScriptableObject 与自定义 Editor 工具构建高解耦架构，支持玩法逻辑、AI 行为与电影级运镜的快速迭代。'
    },
    { 
      name: '计算机视觉与感知', 
      value: 84,
      description: '系统学习计算机图像、神经网络与计算机视觉等课程，实际参与图像分割、多视角几何等方向的实验，并在工程中构建基于 SAM2 的交互式分割与 9D 空间标注工具。'
    },
    { 
      name: 'English & 工程素养', 
      value: 80,
      description: 'PTE 69，可流畅阅读英文技术文档与论文；了解 Docker 基础部署流程，熟练使用 Git 进行版本控制与团队协作。'
    },
    { 
      name: '数据驱动开发与自动化', 
      value: 78,
      description: '通过 Python + Excel 自动化生成 200+ 份周报，构建错词与语法错误趋势分析与可视化；在工程实践中习惯设计监控与可视化模块，让算法效果与系统状态可观测、可调优。'
    },
  ];
};

const getOtherSkills = (lang) => {
  if (lang === 'en') {
    return [
      'Data Visualization & Dashboards',
      '3D Modeling & Asset Optimization Basics',
      'Team Collaboration & Project Management',
      'Docker / Deployment Basics',
      'Scientific Fitness & Long-distance Cycling',
    ];
  }
  return [
    '数据可视化与监控面板',
    '3D 建模与资产优化基础',
    '团队协作与项目推进',
    'Docker / 部署基础',
    '科学健身与长途骑行',
  ];
};

const getProjects = (lang) => {
  if (lang === 'en') {
    return [
      {
        title: 'Intelligent Image Annotation System',
        desc: 'Self-developed 2D+9D intelligent annotation tool based on SAM2, enabling second-level automatic segmentation and pose estimation for SKU objects; built an automated data loop from NeRF reconstruction to Isaac Sim simulation to provide high-precision training data for robotic grasping strategies.',
        tags: ['Python', 'PyTorch', 'SAM2', 'Computer Vision', 'Isaac Sim', 'Tooling'],
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project/autolabel',
        link: 'https://github.com/QperM/DAX_Autolabeling',
      },
      {
        title: '"Sky Era" 2.5D Unity Demo',
        desc: 'Solo full-stack development of the 2.5D RPG "Sky Era". Built an automated AIGC asset pipeline and an event-driven, highly decoupled architecture, plus custom Editor tooling to dramatically shorten iteration cycles.',
        tags: ['Unity', 'C#', 'AIGC Pipeline', 'NavMesh AI', 'Editor Tools'],
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project/projectAGS',
        link: 'https://docs.qq.com/pdf/DYW12anBxamhraVZ0',
      },
      {
        title: 'Light Erosion - FPS Demo',
        desc: 'C++ + UE implementation of weapon system, AI behavior trees, and combat pacing validation, including video and documentation delivery.',
        tags: ['C++', 'Unreal Engine', 'Blueprints', '3D Modeling'],
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project_lighterosion.png',
        link: 'https://docs.qq.com/pdf/DYUFncmp5QkF1cktm',
      },
      {
        title: 'CNS Education Platform Upgrade',
        desc: 'React + Strapi frontend interaction and system optimization, culturally sensitive content visualization.',
        tags: ['React', 'Strapi', 'System Optimization', 'SVG Interaction'],
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cns%20home.jpg',
        link: 'https://docs.qq.com/pdf/DYWRrQ3RnS0pkQVFO',
      },
      {
        title: 'Mosquito Killer',
        desc: 'Android app based on Kotlin, using CameraX to access camera, with custom real-time image processing (color space transformation, dynamic threshold analysis) to identify and highlight small dark static objects in the frame, helping users find mosquitoes.',
        tags: ['Android','Kotlin','CameraX','Image Processing','Material Design'],
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/mosquito%20killer.jpg',
        link: 'https://github.com/QperM/MosquitoKiller',
      },
    ];
  }
  return [
    {
      title: '智能图像标注系统',
      desc: '自主研发基于SAM2的2D+9D智能标注工具，实现SKU物体的秒级自动分割与姿态估计；构建从NeRF重建到Isaac Sim仿真的自动化数据闭环，为机器人抓取策略提供高精度训练数据。',
      tags: ['Python', 'PyTorch', 'SAM2', '计算机视觉', 'Isaac Sim', '工具开发'],
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project/autolabel',
      link: 'https://github.com/QperM/DAX_Autolabeling',
    },
    {
      title: '《空天纪元》2.5D Unity Demo',
      desc: '独立主导2.5D RPG《空天纪元》全栈开发。搭建AIGC资产自动化管线，基于事件驱动架构实现系统高解耦，自研Editor工具链大幅缩短迭代周期。',
      tags: ['Unity', 'C#', 'AIGC 管线', 'NavMesh AI', '编辑器工具'],
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project/projectAGS',
      link: 'https://docs.qq.com/pdf/DYW12anBxamhraVZ0',
    },
    {
      title: '光蚀 - FPS Demo',
      desc: 'C++ + UE 实现武器系统、AI 行为树与战斗节奏验证，含视频与文档交付。',
      tags: ['C++', 'Unreal Engine', '蓝图', '3D 建模'],
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/project_lighterosion.png',
      link: 'https://docs.qq.com/pdf/DYUFncmp5QkF1cktm',
    },
    {
      title: 'CNS 教育平台升级',
      desc: 'React + Strapi 前端交互与系统优化，文化敏感内容可视化呈现。',
      tags: ['React', 'Strapi', '系统优化', 'SVG 交互'],
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cns%20home.jpg',
      link: 'https://docs.qq.com/pdf/DYWRrQ3RnS0pkQVFO',
    },
    {
      title: 'Mosquito Killer',
      desc: '基于 Kotlin 的安卓应用，核心利用 CameraX 调用相机，并通过自定义实时图像处理（色彩空间变换、动态阈值分析），识别并高亮画面中的微小深色静态物体，辅助用户寻找蚊子',
      tags: ['Android','Kotlin','CameraX','图像处理','Material Design'],
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/mosquito%20killer.jpg',
      link: 'https://github.com/QperM/MosquitoKiller',
    },
  ];
};

const getGames = (lang) => {
  if (lang === 'en') {
    return [
      {
        title: 'CrossFire: Mobile (CFM)',
        hours: 'Main Game',
        type: 'FPS · PVP/PVE',
        takeaways: 'Deep understanding of TTK, shooting feedback, and combat pacing. Sensitive to multi-mode maps, balance, and weapon feel. Long-term high-ranked player, researched links between mobile shooting feel, recoil, skill cooldowns, and reload pacing. Ranked #83 globally (12M DAU, top 0.01%), able to reverse-engineer design logic from a player perspective.',
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/cfm.jpg',
      },
      {
        title: 'Steam Single-Player',
        hours: '2000+ Hours',
        type: 'Action / RPG / Roguelike',
        takeaways: 'Focus on narrative pacing, combat loops, and numerical progression. Experienced Souls-like, roguelike, 4X strategy, and design-focused indie games. Personal game library includes 200+ titles. 100% completion of Black Myth: Wukong, Cyberpunk 2077, and more.',
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/steam.jpg',
      },
      {
        title: 'League of Legends (LOL)',
        hours: '3000+ Matches',
        type: 'MOBA',
        takeaways: 'Veteran summoner since Season 4, 3000+ matches total, peak rank Platinum II. Over ten years, fully experienced rune reworks, item system iterations, and map ecosystem evolution. Habitually reverse-engineer mechanism design logic from player behavior—e.g., how minion wave interactions affect map mobility, how vision control shapes level pacing.',
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/lol.jpg',
      },
      {
        title: 'Teamfight Tactics (TFT)',
        hours: '1200+ Games',
        type: 'Mobile / PC',
        takeaways: 'Reached Grandmaster rank 7 times, deeply adapted to multiple seasons of trait, economy, and item system iterations. In fast-paced version updates, continuously observed how tutorial guidance, team recommendations, and UI flow affect strategy learning cost and long-term retention.',
        image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/金铲铲.jpg',
      }
    ];
  }
  return [
    {
      title: '穿越火线：枪战王者（CFM）',
      hours: '深度主游',
      type: 'FPS · PVP/PVE',
      takeaways: '深度理解 TTK（Time-to-Kill）、射击反馈与战斗节奏，对多模式地图布局、数值平衡及武器手感高度敏感。长期高段位玩家，深入研究移动射击手感、后坐力反馈、技能冷却与换弹节奏的耦合关系，在竞技排位中跻身全服第83名（1200万日活，约前0.01%），具备从玩家视角反推设计逻辑的能力。',
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/cfm.jpg',
    },
    {
      title: 'Steam 平台单机',
      hours: '2000+ 小时',
      type: '动作 / RPG / Roguelike',
      takeaways: '关注叙事节奏、战斗循环与数值成长；体验过类魂系、肉鸽、4X策略、设计型独立游戏，自身游戏库涵盖200+款游戏。全成就通关黑神话悟空，赛博朋克2077等游戏。',
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/steam.jpg',
    },
    {
      title: '英雄联盟（LOL）',
      hours: '3000+ 对局数',
      type: 'MOBA',
      takeaways: 'S4 赛季入坑的老召唤师，累计对局 3000+，最高段位铂金 II。十年间完整经历了符文重做、装备系统迭代与地图生态演变，习惯从玩家行为反推机制设计逻辑——比如兵线交互如何影响地图流动性，视野博弈怎样塑造关卡节奏。',
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/lol.jpg',
    },
    {
      title: '金铲铲 & 云顶之弈',
      hours: '1200+ 局',
      type: '移动 / PC',
      takeaways: '7 次达到超凡大师段位，深度适应多个赛季的羁绊、经济与装备系统迭代。在快节奏版本更迭中，持续观察新手引导、阵容推荐与界面动线如何影响策略学习成本与长期留存。',
      image: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/games/金铲铲.jpg',
    }
  ];
};

const getMediaItems = (lang) => {
  if (lang === 'en') {
    return [
      {
        type: 'video',
        poster: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cover/cover1.png',
        title: 'Light Erosion Demo',
        desc: 'Two-minute demo showcasing the first two levels of Light Erosion, experiencing the light energy survival mechanism and weapon system.',
        link: 'https://www.bilibili.com/video/BV1kyiSBAEF3/?share_source=copy_web&vd_source=c9627d7312a8d6ac257099d6f0548af8',
      },
      {
        type: 'video',
        poster: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cover/AGS cover.png',
        title: 'Sky Era Demo',
        desc: 'Demo of the 2.5D RPG "Sky Era", exploring a magic-and-steampunk inspired world and piecing together the lore through items and environmental storytelling.',
        link: 'https://www.bilibili.com/video/BV1TKAfzNEVp?vd_source=c9627d7312a8d6ac257099d6f0548af8',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery1.png',
        title: 'Light Erosion Scene 1',
        desc: 'Atmosphere & lighting – world plunged into darkness, protagonist carries their own light source',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery2.png',
        title: 'Light Erosion Scene 2',
        desc: 'Level pacing checkpoint – teleportation array and teleportation effects',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery3.png',
        title: 'Light Erosion Scene 3',
        desc: 'Entrance seal – initially blocks the player from entering the level',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery4.png',
        title: 'Light Erosion Scene 4',
        desc: 'Multi-layer UI',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery5.png',
        title: 'Light Erosion Scene 5',
        desc: 'Diverse weapons – different mechanics and feel',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery6.png',
        title: 'Light Erosion Scene 6',
        desc: 'Multiple weapons can fire simultaneously with their own fire rates',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery9.png',
        title: 'Sky Era Scene 1',
        desc: 'Main island map and scene layout',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery7.png',
        title: 'Sky Era Scene 2',
        desc: 'Combat scene transitions and UI design',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery8.png',
        title: 'Sky Era Scene 3',
        desc: 'Character–environment interaction and animation design',
      },
      {
        type: 'image',
        src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery10.png',
        title: 'Sky Era Scene 4',
        desc: 'Character–environment interaction and animation design',
      },
    ];
  }
  return [
    {
      type: 'video',
      poster: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cover/cover1.png',
      title: '光蚀 Demo 演示',
      desc: '《光蚀》Demo两分钟展示前两关流程，体验光能生存机制与武器系统。',
      link: 'https://www.bilibili.com/video/BV1kyiSBAEF3/?share_source=copy_web&vd_source=c9627d7312a8d6ac257099d6f0548af8',
    },
    {
      type: 'video',
      poster: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/cover/AGS cover.png',
      title: '空天纪元 Demo 演示',
      desc: '《空天纪元》演示，探索充满魔法与蒸汽朋克的场景，通过物品与场景细节拼凑世界观。',
      link: 'https://www.bilibili.com/video/BV1TKAfzNEVp?vd_source=c9627d7312a8d6ac257099d6f0548af8',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery1.png',
      title: '光蚀场景截图 1',
      desc: '氛围与光影 世界陷入黑暗 主角自带光源',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery2.png',
      title: '光蚀场景截图 2',
      desc: '关卡节奏检查点 传送阵与传送特效',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery3.png',
      title: '光蚀场景截图 3',
      desc: '入口封印 初始会阻挡玩家进入关卡',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery4.png',
      title: '光蚀场景截图 4',
      desc: '多层级UI',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery5.png',
      title: '光蚀场景截图 5',
      desc: '多样化武器 不同的机制与手感',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery6.png',
      title: '光蚀场景截图 6',
      desc: '多把武器可同时开火并拥有自身开火频率',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery9.png',
      title: '空天纪元场景截图 1',
      desc: '主岛地图与场景设计',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery7.png',
      title: '空天纪元场景截图 2',
      desc: '战斗场景的切换与UI设计',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery8.png',
      title: '空天纪元场景截图 3',
      desc: '角色与场景的交互与动画设计',
    },
    {
      type: 'image',
      src: 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/gallery/gallery10.png',
      title: '空天纪元场景截图 4',
      desc: '角色与场景的交互与动画设计',
    },
  ];
};

const getContacts = (lang) => {
  if (lang === 'en') {
    return [
      { label: 'Email', value: 'junyu.ma.resume@gmail.com', href: 'mailto:junyu.ma.resume@gmail.com' },
      { label: 'Phone', value: '15535578110', href: 'tel:15535578110' },
      { label: 'Hometown', value: 'Changzhi, Shanxi' },
      { label: 'Education', value: 'Master\'s Degree (UNSW, AI Stream)' },
    ];
  }
  return [
    { label: '邮箱', value: 'junyu.ma.resume@gmail.com', href: 'mailto:junyu.ma.resume@gmail.com' },
    { label: '电话', value: '15535578110', href: 'tel:15535578110' },
    { label: '籍贯', value: '山西长治' },
    { label: '学历', value: '硕士研究生（UNSW·人工智能方向）' },
  ];
};

function App() {
  const [language, setLanguage] = useState('zh'); // 'zh' or 'en'
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, src: '', type: 'image', title: '' });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const hasSwiped = useRef(false);
  const touchStartY = useRef(null);
  const touchEndY = useRef(null);

  const t = translations[language];
  const experiences = getExperiences(language);
  const skills = getSkills(language);
  const otherSkills = getOtherSkills(language);
  const projects = getProjects(language);
  const games = getGames(language);
  const mediaItems = getMediaItems(language);
  const contacts = getContacts(language);
  const [gameIndex, setGameIndex] = useState(() => Math.floor(games.length / 2));
  const [projectIndex, setProjectIndex] = useState(0);
  const [showGames, setShowGames] = useState(false);
  const imageItems = mediaItems.filter((item) => item.type === 'image');
  const videoItems = mediaItems.filter((item) => item.type === 'video');
  const marqueeImages = imageItems.length > 0 ? [...imageItems, ...imageItems] : [];
  const galleryTrackRef = useRef(null);

  const visibleProjects =
    projects.length <= 3
      ? projects
      : Array.from({ length: 3 }, (_, i) => projects[(projectIndex + i) % projects.length]);

  useEffect(() => {
    const track = galleryTrackRef.current;
    if (!track || marqueeImages.length === 0) return;

    let offset = 0;
    let frameId;

    const step = () => {
      offset += 0.3; // 控制滚动速度（像素/帧）
      const totalWidth = track.scrollWidth / 2;
      if (totalWidth > 0 && offset >= totalWidth) {
        offset = 0;
      }
      track.style.transform = `translateX(-${offset}px)`;
      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [marqueeImages.length]);

  const navItems = [
    { label: t.nav.home, href: '#home' },
    { label: t.nav.about, href: '#about' },
    { label: t.nav.experience, href: '#experience' },
    { label: t.nav.skills, href: '#skills' },
    { label: t.nav.portfolio, href: '#portfolio' },
    { label: t.nav.games, href: '#games' },
    { label: t.nav.gallery, href: '#gallery' },
    { label: t.nav.contact, href: '#contact' },
  ];

  const handleScroll = (href) => {
    if (href === '#games') {
      setShowGames(true);
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  // 移动端滑动处理
  const minSwipeDistance = 50; // 最小滑动距离

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndY.current = null;
    hasSwiped.current = false;
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    touchEndY.current = e.targetTouches[0].clientY;
    // 如果检测到明显的垂直滑动，不阻止默认行为（允许页面滚动）
    if (touchStartY.current !== null) {
      const deltaY = Math.abs(e.targetTouches[0].clientY - touchStartY.current);
      const deltaX = Math.abs(e.targetTouches[0].clientX - (touchStart || 0));
      // 如果垂直滑动距离明显大于水平滑动，允许默认滚动行为
      if (deltaY > deltaX && deltaY > 10) {
        return; // 不阻止默认行为，允许页面滚动
      }
      // 如果是水平滑动，阻止默认行为以避免页面滚动
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || touchStartY.current === null || touchEndY.current === null) {
      hasSwiped.current = false;
      touchStartY.current = null;
      touchEndY.current = null;
      return;
    }
    
    const deltaX = touchStart - touchEnd;
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);
    
    // 判断是水平滑动还是垂直滑动
    // 如果垂直滑动距离大于水平滑动距离，或者垂直滑动距离超过阈值，则认为是垂直滑动
    const isVerticalSwipe = deltaY > Math.abs(deltaX) && deltaY > 20;
    const isHorizontalSwipe = Math.abs(deltaX) > deltaY && Math.abs(deltaX) > minSwipeDistance;
    
    if (isHorizontalSwipe && !isVerticalSwipe) {
      const isLeftSwipe = deltaX > minSwipeDistance;
      const isRightSwipe = deltaX < -minSwipeDistance;

      if (isLeftSwipe) {
        // 向左滑动，显示下一张
        hasSwiped.current = true;
        setGameIndex((idx) => (idx + 1) % games.length);
      } else if (isRightSwipe) {
        // 向右滑动，显示上一张
        hasSwiped.current = true;
        setGameIndex((idx) => (idx - 1 + games.length) % games.length);
      }
    } else {
      hasSwiped.current = false;
    }
    
    // 重置触摸状态
    setTimeout(() => {
      setTouchStart(null);
      setTouchEnd(null);
      touchStartY.current = null;
      touchEndY.current = null;
      hasSwiped.current = false;
    }, 100);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });


    const templateParams = {
      from_name: e.target.name.value,
      from_email: e.target.email.value,
      message: e.target.message.value,
      reply_to: e.target.email.value, 
    };

    try {
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_uifo9q6',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_stan71c',
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '11oS_enHDgk3u1Rrc'
      );

      console.log('EmailJS 响应:', response);
      setFormStatus({ 
        type: 'success', 
        message: t.contact.successMessage
      });
      e.target.reset();
    } catch (error) {
      console.error('发送失败详情:', error);
      console.error('错误代码:', error.code);
      console.error('错误消息:', error.text);
      setFormStatus({ 
        type: 'error', 
        message: t.contact.errorMessage.replace('{error}', error.text || error.message)
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="text-slate-900 bg-transparent">
      <header className="fixed inset-x-0 top-0 z-40">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-2xl px-4 py-3 mt-3 glass shadow-lg">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-emerald-400 shadow-md" />
            <div>
              <p className="text-lg font-semibold text-slate-900">{language === 'zh' ? '马君羽' : 'Junyu Ma'}</p>
            </div>
          </div>
          <nav className="hidden items-center gap-4 text-sm font-medium text-slate-700 md:flex">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleScroll(item.href)}
                className="rounded-lg px-3 py-2 transition hover:text-indigo-600 hover:bg-indigo-50"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:text-indigo-600 hover:bg-indigo-50"
            >
              <span className={language === 'zh' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                中文
              </span>
              <span className="text-slate-400">/</span>
              <span className={language === 'en' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                English
              </span>
            </button>
            <button
              onClick={() => handleScroll('#contact')}
              className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2 text-white shadow hover:shadow-lg transition"
            >
              {t.nav.contactMe}
            </button>
          </nav>
          <button
            className="md:hidden rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="切换导航菜单"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        {menuOpen && (
          <div className="mx-4 mt-2 rounded-2xl bg-white shadow-lg md:hidden">
            <div className="flex flex-col divide-y divide-slate-100">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleScroll(item.href)}
                  className="px-4 py-3 text-left text-slate-700 hover:bg-indigo-50"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
                className="flex items-center gap-1 px-4 py-3 text-left text-slate-700 hover:bg-indigo-50"
              >
                <span className={language === 'zh' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                  中文
                </span>
                <span className="text-slate-400">/</span>
                <span className={language === 'en' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                  English
                </span>
              </button>
              <button
                onClick={() => handleScroll('#contact')}
                className="px-4 py-3 text-left font-semibold text-indigo-600 hover:bg-indigo-50"
              >
                {t.nav.contactMe}
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="pt-28 md:pt-32">
        <section
          id="home"
          className="relative isolate overflow-hidden px-4 pb-20 pt-16 md:pt-24"
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-50 via-white to-emerald-50" />
          <div className="mx-auto flex max-w-6xl flex-col gap-12 md:flex-row md:items-center">
            <div className="flex-1 space-y-6">
              <h1 className="text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
                {t.home.greeting}<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500"> {language === 'zh' ? '马君羽' : 'Junyu Ma'}</span>
              </h1>
              <p className="text-lg text-slate-600 md:text-xl">
                {t.home.intro}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleScroll('#contact')}
                  className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 text-white shadow-lg shadow-indigo-200 transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                  {t.home.contactBtn}
                </button>
                <button
                  onClick={() => handleScroll('#portfolio')}
                  className="rounded-full border border-slate-300 px-6 py-3 text-slate-800 transition hover:border-indigo-300 hover:text-indigo-600 hover:-translate-y-0.5"
                >
                  {t.home.viewWorkBtn}
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {t.home.stats.map((item) => (
                  <div
                    key={item.label}
                    className="glass rounded-xl p-4 shadow-sm ring-1 ring-white/60"
                  >
                    <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{item.value}</p>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative mx-auto max-w-sm">
                <div className="absolute inset-0 -z-10 blur-3xl bg-indigo-200/50 rounded-full" />
                <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/80 shadow-xl shadow-indigo-200">
                  <img
                    src="https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/profile.jpg"
                    alt={language === 'zh' ? '马君羽' : 'Junyu Ma'}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute -left-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400 via-cyan-400 to-indigo-500 opacity-60 blur-2xl" />
                <div className="absolute -right-10 -bottom-10 h-24 w-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 opacity-50 blur-2xl" />
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="px-4 py-16 md:py-20">
          <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.2fr,0.8fr]">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">{t.about.title}</h2>
              <p className="text-base leading-7 text-slate-600">
                {t.about.desc1}
              </p>
              <p className="text-base leading-7 text-slate-600">
                {t.about.desc2}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {t.about.infoCards.map((card) => (
                  <div key={card.label} className="glass rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-indigo-600">{card.label}</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{card.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass h-fit space-y-4 rounded-2xl p-6 shadow-lg">
              <p className="text-sm font-semibold text-indigo-600">{t.about.contactTitle}</p>
              <div className="grid gap-3 text-sm text-slate-700">
                {contacts.map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-lg bg-white/70 px-4 py-3">
                    <span className="font-medium text-slate-600">{item.label}</span>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-slate-900">{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-sm text-white shadow-md">
                {t.about.availability}
              </div>
            </div>
          </div>
        </section>

        <section id="experience" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-3xl font-bold text-slate-900">{t.experience.title}</h2>
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
                {t.experience.badge}
              </span>
            </div>
            <div className="relative space-y-8 before:absolute before:left-[10px] before:top-2 before:h-full before:w-0.5 before:bg-indigo-100 md:before:left-1/2 md:before:-translate-x-1/2">
              {experiences.map((exp, idx) => (
                <div
                  key={exp.title}
                  className={`relative md:w-1/2 ${idx % 2 === 0 ? 'md:ml-auto md:pl-10' : 'md:pr-10'}`}
                >
                  <div className="absolute left-0 top-2 h-4 w-4 rounded-full border-2 border-white bg-indigo-500 shadow md:left-auto md:right-[-22px]" />
                  <div className="glass rounded-2xl p-6 shadow-lg">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                      {exp.date}
                    </p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{exp.title}</h3>
                    <p className="text-sm text-slate-500">{exp.subtitle}</p>
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
                      {exp.bullets.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-indigo-400" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        

        <section id="skills" className="px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-10">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.skills.title}</h2>
              
            </div>
            <div className="grid gap-8 lg:grid-cols-[1.3fr,0.7fr]">
              <div className="space-y-5">
                {skills.map((skill, idx) => {
                  const bubbleStyles = [
                    'bg-gradient-to-r from-indigo-500 to-cyan-400 text-white rotate-[-2deg] shadow-xl',
                    'glass border border-indigo-100 text-slate-800 shadow-lg',
                    'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 text-white rotate-1 shadow-xl',
                    'bg-white text-slate-900 shadow-md border border-slate-200',
                    'bg-gradient-to-r from-emerald-400 to-teal-500 text-white rotate-[-1deg] shadow-xl',
                    'glass border border-purple-100 text-slate-800 shadow-lg',
                  ];
                  const sizeStyles = [
                    'rounded-[28px] px-5 py-4',
                    'rounded-[32px] px-6 py-5',
                    'rounded-[24px] px-5 py-3',
                    'rounded-[36px] px-7 py-5',
                  ];
                  const shape = sizeStyles[idx % sizeStyles.length];
                  const color = bubbleStyles[idx % bubbleStyles.length];
                  const align = idx % 2 === 0 ? 'justify-start' : 'justify-end';
                  const isGradient = color.includes('gradient');

                  return (
                    <div
                      key={skill.name}
                      className={`flex items-start gap-3 ${align} transition-transform hover:-translate-y-1`}
                    >
                      <div
                        className={`max-w-[88%] ${shape} ${color}`}
                        style={{
                          borderRadius: idx % 3 === 0 ? '40px 28px 32px 36px' : undefined,
                        }}
                      >
                        <p className="text-base font-semibold">{skill.name}</p>
                        <p className={`${isGradient ? 'text-white/85' : 'text-slate-600'} mt-2 text-sm leading-6`}>
                          {skill.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="glass rounded-2xl p-6 shadow-lg">
                <p className="text-lg font-semibold text-indigo-600">{t.skills.otherSkills}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {otherSkills.map((skill) => (
                    <span
                      key={skill}
                      className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-lg font-medium text-indigo-700"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="portfolio" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.portfolio.title}</h2>
            </div>
            <div className="relative">
              <div className="absolute -left-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
                <button
                  onClick={() =>
                    setProjectIndex((idx) => (idx - 1 + projects.length) % projects.length)
                  }
                  className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:-translate-x-0.5 hover:bg-white transition"
                  aria-label={language === 'zh' ? '上一组项目' : 'Previous projects'}
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              <div className="absolute -right-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
                <button
                  onClick={() =>
                    setProjectIndex((idx) => (idx + 1) % projects.length)
                  }
                  className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:translate-x-0.5 hover:bg-white transition"
                  aria-label={language === 'zh' ? '下一组项目' : 'Next projects'}
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div className="grid gap-6 md:grid-cols-3 md:min-h-[430px]">
                {visibleProjects.map((project) => (
                <article
                  key={project.title}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white/90 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent opacity-60" />
                  </div>
                    <div className="space-y-3 p-5 md:min-h-[280px]">
                    <h3 className="text-lg font-semibold text-slate-900">{project.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">{project.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href={project.link}
                      target={project.link.startsWith('#') ? '_self' : '_blank'}
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 transition hover:gap-2"
                    >
                      {t.portfolio.viewDetails}
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                </article>
              ))}
              </div>
            </div>
          </div>
        </section>

        <section id="games" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">{t.games.title}</h2>
                <p className="text-sm text-slate-600">{t.games.subtitle}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
                  {t.games.badge}
                </span>
                <button
                  onClick={() => setShowGames((v) => !v)}
                  className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 transition"
                >
                  {showGames ? (language === 'zh' ? '收起' : 'Hide') : (language === 'zh' ? '展开' : 'Show')}
                </button>
              </div>
            </div>
            {showGames && (
              <div className="relative">
                <div className="absolute -left-4 top-1/2 z-20 hidden -translate-y-1/2 md:block">
                  <button
                    onClick={() => setGameIndex((idx) => (idx - 1 + games.length) % games.length)}
                    className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:-translate-x-0.5 hover:bg-white transition"
                    aria-label={t.games.prevGame}
                  >
                    <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                </div>
                <div className="absolute -right-4 top-1/2 z-20 hidden -translate-y-1/2 md:block">
                  <button
                    onClick={() => setGameIndex((idx) => (idx + 1) % games.length)}
                    className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:translate-x-0.5 hover:bg-white transition"
                    aria-label={t.games.nextGame}
                  >
                    <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div 
                  className="relative h-[480px] overflow-hidden py-8"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {games.map((game, idx) => {
                    const delta = (idx - gameIndex + games.length) % games.length;
                    const position = delta === 0 ? 'center' : delta === 1 ? 'right' : delta === games.length - 1 ? 'left' : 'hidden';
                    if (position === 'hidden') return null;

                    const rotate = position === 'center' ? 0 : position === 'right' ? 6 : -6;
                    const scale = position === 'center' ? 1.08 : 0.92;
                    const translateX = position === 'center' ? 0 : position === 'right' ? 210 : -210;
                    const zIndex = position === 'center' ? 30 : 20;
                    const opacity = position === 'center' ? 1 : 0.88;

                    return (
                      <button
                        type="button"
                        onClick={(e) => {
                          // 如果是滑动操作，不触发点击
                          if (hasSwiped.current) {
                            e.preventDefault();
                            return;
                          }
                          setGameIndex(idx);
                        }}
                        key={`${game.title}-${idx}`}
                        className="group absolute left-1/2 top-4 h-[420px] w-[320px] -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-2xl transition duration-300 focus:outline-none"
                        style={{
                          transform: `translate(calc(-50% + ${translateX}px), 0) rotate(${rotate}deg) scale(${scale})`,
                          zIndex,
                          opacity,
                          cursor: position === 'center' ? 'default' : 'pointer',
                        }}
                      >
                        <div className="absolute inset-0 overflow-hidden rounded-3xl">
                          <div
                            className="absolute inset-0 rounded-3xl bg-cover bg-center transition duration-500 group-hover:scale-105"
                            style={{
                              backgroundImage: `url(${game.image || 'https://resume-majunyu-2026.oss-cn-hangzhou.aliyuncs.com/images/game-placeholder.jpg'})`,
                            }}
                          />
                          <div
                            className="absolute inset-0 rounded-3xl bg-gradient-to-b from-black/75 via-black/55 to-black/25 opacity-85 transition duration-300 group-hover:opacity-0"
                          />
                        </div>
                        <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 blur-2xl opacity-80" />
                        <div className="relative flex items-center justify-between gap-3 text-white">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-200">{t.games.playRecord}</p>
                            <h3 className="mt-1 text-xl font-semibold text-white">{game.title}</h3>
                          </div>
                          <span className="rounded-full bg-white/20 px-3 py-1 text-[12px] font-semibold text-white shadow">
                            {game.hours}
                          </span>
                        </div>
                        <div className="relative mt-3 inline-flex rounded-full bg-white/15 px-3 py-1 text-[12px] font-semibold text-white backdrop-blur">
                          {game.type}
                        </div>
                        <p className={`relative mt-3 font-medium leading-7 text-white drop-shadow-md ${language === 'en' ? 'text-[12px]' : 'text-[15px]'}`}>
                          {game.takeaways}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="gallery" className="px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-10">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.gallery.title}</h2>
            </div>

            {/* 视频区域保持网格展示 */}
            {videoItems.length > 0 && (
            <div className="grid gap-6 md:grid-cols-3">
                {videoItems.map((item) => (
                <div
                  key={item.title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white/80 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
                >
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative block h-52 w-full overflow-hidden"
                    >
                      <img
                        src={item.poster}
                        alt={item.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-900/50 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="rounded-full bg-white/90 p-4 shadow-lg transition group-hover:scale-110">
                          <svg className="h-8 w-8 text-slate-900" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </a>
                    <div className="space-y-2 p-4">
                      <p className="text-sm font-semibold text-indigo-600">{t.gallery.video}</p>
                      <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
                        </div>
                      )}

            {/* 图片横向轮播：连贯滚动，头尾衔接 */}
            {imageItems.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {language === 'zh' ? '图片预览（自动轮播）' : 'Image Preview (Auto Carousel)'}
                  </h3>
                </div>
                <div className="relative overflow-hidden rounded-2xl bg-white/70 px-2 py-4 shadow-inner">
                  <div
                    ref={galleryTrackRef}
                    className="inline-grid grid-rows-2 auto-cols-[330px] grid-flow-col gap-4"
                  >
                    {marqueeImages.map((item, idx) => {
                      const isSecondRow = idx % 2 === 1;
                      return (
                        <div
                          key={`${item.title}-${idx}`}
                          className="relative"
                          style={isSecondRow ? { transform: 'translateX(66.666%)' } : undefined}
                        >
                          <div className="group overflow-hidden rounded-2xl border border-slate-100 bg-white/80 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                    <button
                      type="button"
                              onClick={() =>
                                setLightbox({ open: true, src: item.src, type: 'image', title: item.title })
                              }
                              className="relative block w-full overflow-hidden"
                              style={{ aspectRatio: '4 / 3' }}
                    >
                      <img
                        src={item.src}
                        alt={item.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 transition group-hover:opacity-100" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                                <span className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-900 shadow">
                          {t.gallery.clickToEnlarge}
                        </span>
                      </div>
                    </button>
                            <div className="space-y-1 p-3">
                              <p className="text-xs font-semibold text-indigo-600">{t.gallery.image}</p>
                              <h4 className="text-sm font-semibold text-slate-900 line-clamp-1">{item.title}</h4>
                              <p className="text-xs text-slate-600 line-clamp-2">{item.desc}</p>
                  </div>
                </div>
            </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="contact" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[1fr,1fr]">
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-slate-900">{t.contact.title}</h2>
              <p className="text-slate-600">
                {t.contact.desc}
              </p>
              <div className="grid gap-3 text-sm">
                {contacts.map((item) => (
                  <div key={item.label} className="flex items-center gap-2 rounded-xl bg-indigo-50/80 px-4 py-3 text-indigo-800">
                    <span className="font-semibold">{item.label}</span>
                    <span className="text-slate-700">·</span>
                    {item.href ? (
                      <a href={item.href} className="underline decoration-indigo-300 underline-offset-4 hover:decoration-indigo-500">
                        {item.value}
                      </a>
                    ) : (
                      <span>{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">{t.contact.leaveMessage}</h3>
              {formStatus.message && (
                <div
                  className={`mt-4 rounded-lg px-4 py-3 text-sm ${
                    formStatus.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {formStatus.message}
                </div>
              )}
              <form className="mt-4 space-y-4" onSubmit={handleFormSubmit}>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourName}</span>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourEmail}</span>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourMessage}</span>
                  <textarea
                    name="message"
                    rows="4"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-white font-semibold shadow-lg shadow-indigo-200 transition hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isSubmitting ? t.contact.sending : t.contact.sendMessage}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="px-4 pb-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 rounded-2xl bg-white px-6 py-6 text-slate-900 shadow-lg md:flex-row">
          <p className="text-sm">&copy; 2024 {language === 'zh' ? '马君羽' : 'Junyu Ma'} · {t.footer.copyright}</p>
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-800">Python / PyTorch / Unity</span>
            {t.footer.tags.map((tag, idx) => (
              <span key={idx} className="rounded-full bg-slate-200 px-3 py-1 text-slate-800">{tag}</span>
            ))}
          </div>
        </div>
      </footer>

      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setLightbox({ open: false, src: '', type: 'image', title: '' })}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-slate-600 shadow hover:bg-white"
              onClick={() => setLightbox({ open: false, src: '', type: 'image', title: '' })}
              aria-label={t.lightbox.close}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
            {lightbox.type === 'image' ? (
              <img src={lightbox.src} alt={lightbox.title} className="h-full w-full max-h-[90vh] object-contain" />
            ) : (
              <video src={lightbox.src} controls className="h-full w-full max-h-[90vh] object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

